// ============================================================================
// LOS CRITERIOS DE CALIFICACIÓN — la pestaña «Calificación» de Entrenamiento.
// T-CAL-5 (R-CRI-01 · R-CRI-02) · invariantes I-137, I-138, I-139.
// Plan: `Chat AI - Voz y Calificacion/sdd/actas/PLAN-T-CAL-5.md`.
//
// El dueño cambia con qué se califica a un cliente: cuántos puntos da cada dato,
// desde cuántos puntos es 🔥 / tibio, cuándo se enfría y cuándo pasa a «Dormido».
// El worker lee estos criterios EN CADA TURNO (`calificador.ts`), así que rigen
// desde el próximo mensaje de cada cliente.
//
// ⛔ LA PUERTA NO ESTÁ AQUÍ: está dentro de `chatai_app.guardar_criterios` y
//    `chatai_app.volver_a_criterios` (migr `0076`, `soy_victorix() or
//    es_dueno(p_tenant)`), con la lista cerrada de llaves y sus rangos. Esta pantalla
//    solo deja de ofrecer lo que la base va a rechazar.
// ⚠️ `puedeEditar` ENUMERA a quien puede (`victorix`/`dueno`); jamás
//    `rol !== "agente"` (abriría la puerta sola al primer rol nuevo). Mismo criterio
//    que `Guion.tsx`.
// ⛔ La regla dura del caliente (producto + cotización) NO es editable: se explica
//    con una frase fija.
// ⛔ Se mandan SOLO las llaves que cambiaron: la base fusiona sobre la fila vigente,
//    así `etapas`, `dormido_desde` y lo demás ni viajan.
// ⭐ T-CAL-21 (I-181, I-182) · la sección «Notas de voz»: los DOS momentos (lista cerrada,
//    no se crean ni se borran) con su guion, la prueba A/B y el gasto del mes.
//    ⛔ Lo que se guarda es EXACTAMENTE lo que lee el bot: `voz_momentos` (se reemplaza
//    entero en la base, 0085) y `voz_ab`. JAMÁS `voz_momentos_desde` (lo pone la base la
//    primera vez) ni `config.voz` (presupuesto y encendido: solo Victorix, baranda 0081).
//    ⛔ Un momento apagado y sin guion NO viaja (la base exige guion 1…300 en todo momento
//    presente); si no queda ninguno, viaja `{}` = sin momentos = la puerta por temperatura.
//    ⛔ El gasto del mes (`voz_del_mes`, 0086) solo lo ven el dueño y Victorix: al agente
//    ni se le pide.
// ============================================================================
import { useEffect, useId, useRef, useState } from "react";
import { RotateCcw } from "lucide-react";
import { Aviso, Boton, Interruptor, SkeletonLista } from "./piezas.tsx";
import {
  guardarCriterios, leerCriteriosCalificacion, leerVersionesCriterios, leerVozDelMes, leerVozDelNegocio,
  volverACriterios,
} from "./datos.ts";
import type { ContextoPanel, FilaCriterios, VersionCriterios, VozDelMes } from "./tipos.ts";

/** Los diez criterios que suman puntos, en palabras de negocio (la lista cerrada del plan). */
export const CRITERIOS_PUNTOS: readonly { llave: string; nombre: string; ejemplo: string }[] = [
  { llave: "producto",         nombre: "Dijo qué producto quiere",    ejemplo: "«busco un sofá cama»" },
  { llave: "pidio_cotizacion", nombre: "Pidió cotización",            ejemplo: "«¿cuánto me sale?»" },
  { llave: "ciudad",           nombre: "Dijo la ciudad",              ejemplo: "«estoy en Manizales»" },
  { llave: "plazo_30_dias",    nombre: "Lo quiere antes de un mes",   ejemplo: "«lo necesito este mes»" },
  { llave: "forma_pago",       nombre: "Dijo cómo va a pagar",        ejemplo: "«pago con tarjeta»" },
  { llave: "presupuesto",      nombre: "Dijo su presupuesto",         ejemplo: "«tengo como 2 millones»" },
  { llave: "pidio_fotos",      nombre: "Pidió fotos",                 ejemplo: "«¿me mandas fotos?»" },
  { llave: "decide_con",       nombre: "Dijo con quién decide",       ejemplo: "«lo miro con mi esposa»" },
  { llave: "mensajes_cliente", nombre: "Escribió varios mensajes",    ejemplo: "desde el mínimo de abajo" },
  { llave: "respondio_rapido", nombre: "Contestó rápido",             ejemplo: "en menos de los minutos de abajo" },
];

type Sueltos = "dias_tibio" | "dias_frio" | "dias_cotizacion" | "minimo_mensajes" | "minutos_respuesta" | "horas_dormido";

/** Los números sueltos, con su frase y su unidad. Los rangos de verdad los pone la base. */
const SUELTOS: readonly { llave: Sueltos; nombre: string; unidad: string; min: number; max?: number }[] = [
  { llave: "dias_tibio",        nombre: "Días sin escribir para que un 🔥 baje a tibio",     unidad: "días",     min: 1 },
  { llave: "dias_frio",         nombre: "Días sin escribir para que pase a frío",            unidad: "días",     min: 1 },
  { llave: "dias_cotizacion",   nombre: "Días que cuenta una cotización pedida",             unidad: "días",     min: 1 },
  { llave: "minimo_mensajes",   nombre: "Mensajes suyos para contar «escribió varios»",      unidad: "mensajes", min: 1, max: 20 },
  { llave: "minutos_respuesta", nombre: "Minutos para contar «contestó rápido»",             unidad: "minutos",  min: 1, max: 120 },
  { llave: "horas_dormido",     nombre: "Horas sin contestar para pasar a «Dormido»",        unidad: "horas",    min: 24, max: 72 },
];

/**
 * ⭐ T-CAL-11 · desde qué temperatura el bot manda notas de voz (`voz_desde`). Ausente en
 * los criterios = «nunca» (así lo lee el worker). El 🔥 es «caliente».
 */
export const VOZ_DESDE: readonly { valor: "nunca" | "tibio" | "caliente"; nombre: string }[] = [
  { valor: "nunca",    nombre: "Nunca" },
  { valor: "tibio",    nombre: "Desde tibio" },
  { valor: "caliente", nombre: "Solo 🔥" },
];

/**
 * ⭐ T-CAL-21 · los momentos de la voz: la LISTA CERRADA de la base (`voz_momentos`,
 * check `criterios_voz_momentos_valida`, 0085), en el orden en que ocurren.
 */
export const MOMENTOS_VOZ: readonly { llave: "recomendacion" | "compra"; nombre: string; corto: string }[] = [
  { llave: "recomendacion", nombre: "Cuando el cliente responde por segunda vez (recomendación)", corto: "recomendación" },
  { llave: "compra",        nombre: "Cuando el cliente quiere comprar",                           corto: "compra" },
];
type Momento = (typeof MOMENTOS_VOZ)[number]["llave"];
/** El largo máximo de un guion (el mismo de la base). */
export const GUION_MAX = 300;

/** Lo que el dueño NO puede cambiar de la voz (reglas fijas del bot, T-CAL-11/20). */
const VOZ_FIJO: readonly string[] = [
  "Los precios, los links y los datos de pago siempre van por escrito.",
  "Sin audio cuando ya atiende un asesor. Fuera de horario sí suena; si hay que pasar a un asesor, el bot dice cuándo le escriben.",
  "Cada momento suena una sola vez por cliente.",
  "La recomendación solo suena en conversaciones nuevas.",
];

interface BorradorMomento { activa: boolean; guion: string }

/** Lo que se edita: todo como texto (lo que hay en la caja), para poder avisar de la caja vacía. */
interface Borrador {
  puntos: Record<string, string>;
  tibio: string;
  caliente: string;
  sueltos: Record<Sueltos, string>;
  /** ⭐ T-CAL-11 · `voz_desde` (ausente ⇒ «nunca») y `voz_max_por_dia` (texto de la caja). */
  vozDesde: string;
  vozMax: string;
  /** ⭐ T-CAL-21 · cada momento (ausente ⇒ apagado y sin guion) y la prueba A/B (ausente ⇒ no). */
  momentos: Record<Momento, BorradorMomento>;
  vozAb: boolean;
}

const txt = (v: unknown): string => (typeof v === "number" && Number.isFinite(v) ? String(v) : "");

function aBorrador(c: Record<string, any>): Borrador {
  const puntos: Record<string, string> = {};
  for (const { llave } of CRITERIOS_PUNTOS) puntos[llave] = txt(c?.puntos?.[llave]);
  const sueltos = {} as Record<Sueltos, string>;
  for (const { llave } of SUELTOS) sueltos[llave] = txt(c?.[llave]);
  const vozDesde = VOZ_DESDE.some((v) => v.valor === c?.voz_desde) ? String(c.voz_desde) : "nunca";
  const momentos = {} as Record<Momento, BorradorMomento>;
  for (const { llave } of MOMENTOS_VOZ) {
    const m = c?.voz_momentos?.[llave];
    momentos[llave] = { activa: m?.activa === true, guion: typeof m?.guion === "string" ? m.guion : "" };
  }
  return { puntos, tibio: txt(c?.umbrales?.tibio), caliente: txt(c?.umbrales?.caliente), sueltos,
           vozDesde, vozMax: txt(c?.voz_max_por_dia), momentos, vozAb: c?.voz_ab === true };
}

/**
 * ⭐ T-CAL-21 · el `voz_momentos` que resulta del borrador, con la forma EXACTA que exige la
 * base: `{activa, guion}` por momento presente. Un momento apagado y sin guion no viaja.
 * `{}` = sin momentos (la puerta por temperatura de antes).
 */
function momentosEfectivos(b: Borrador): Record<string, { activa: boolean; guion: string }> {
  const r: Record<string, { activa: boolean; guion: string }> = {};
  for (const { llave } of MOMENTOS_VOZ) {
    const m = b.momentos[llave];
    if (m.activa || m.guion.trim() !== "") r[llave] = { activa: m.activa, guion: m.guion };
  }
  return r;
}

/** Por qué un momento no se puede guardar así (la misma regla de la base), o null. */
function motivoDelMomento(m: BorradorMomento): string | null {
  if ([...m.guion].length > GUION_MAX) return `El guion tiene ${[...m.guion].length} caracteres: el máximo es ${GUION_MAX}.`;
  if (m.activa && m.guion.trim() === "") return "Escribe qué debe decir el audio para poder encenderlo.";
  return null;
}

/**
 * Lo que cambió entre lo leído y lo escrito, con la forma anidada que espera la base.
 * Devuelve el error en palabras si una caja que cambió quedó vacía o no es un número.
 */
function cambiosDeCriterios(antes: Borrador, ahora: Borrador): { editables: Record<string, unknown>; error: string | null } {
  const ed: Record<string, any> = {};
  const num = (s: string): number | null => (s.trim() === "" || !Number.isFinite(Number(s)) ? null : Number(s));
  const falta = (nombre: string) => `Escribe un número en «${nombre}».`;
  for (const { llave, nombre } of CRITERIOS_PUNTOS) {
    if (ahora.puntos[llave] === antes.puntos[llave]) continue;
    const n = num(ahora.puntos[llave]);
    if (n === null) return { editables: {}, error: falta(nombre) };
    (ed.puntos ??= {})[llave] = n;
  }
  for (const [k, nombre] of [["tibio", "Tibio desde"], ["caliente", "🔥 desde"]] as const) {
    if (ahora[k] === antes[k]) continue;
    const n = num(ahora[k]);
    if (n === null) return { editables: {}, error: falta(nombre) };
    (ed.umbrales ??= {})[k] = n;
  }
  for (const { llave, nombre } of SUELTOS) {
    if (ahora.sueltos[llave] === antes.sueltos[llave]) continue;
    const n = num(ahora.sueltos[llave]);
    if (n === null) return { editables: {}, error: falta(nombre) };
    ed[llave] = n;
  }
  // ⭐ T-CAL-11 · la voz del bot: la base valida la lista (nunca|tibio|caliente) y el rango 0…10.
  if (ahora.vozDesde !== antes.vozDesde) ed.voz_desde = ahora.vozDesde;
  if (ahora.vozMax !== antes.vozMax) {
    const n = num(ahora.vozMax);
    if (n === null) return { editables: {}, error: falta("Máximo de audios por cliente al día") };
    ed.voz_max_por_dia = n;
  }
  // ⭐ T-CAL-21 · los momentos (se mandan ENTEROS si algo cambió) y la A/B. Nada más de la voz.
  for (const { llave, nombre } of MOMENTOS_VOZ) {
    const motivo = motivoDelMomento(ahora.momentos[llave]);
    if (motivo) return { editables: {}, error: `En «${nombre}»: ${motivo}` };
  }
  const momentosAhora = momentosEfectivos(ahora);
  if (JSON.stringify(momentosAhora) !== JSON.stringify(momentosEfectivos(antes))) ed.voz_momentos = momentosAhora;
  if (ahora.vozAb !== antes.vozAb) ed.voz_ab = ahora.vozAb;
  return { editables: ed, error: null };
}

function fechaYHora(iso: string): string {
  return new Date(iso).toLocaleString("es-CO", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

/** Quién dejó una versión. Mismo criterio (y mismo hueco declarado) que `Guion.tsx`. */
function quienLaDejo(v: VersionCriterios, usuarioId: string | null): string {
  if (v.creado_por && usuarioId && v.creado_por === usuarioId) return "tú";
  return "Victorix";
}

/** Lo que dijo la base, en palabras. El 22023 ya viene en palabras desde la 0076. */
function explicarFallo(e: any): string {
  const code = e?.code ?? "";
  if (code === "42501") return "Tu cuenta no puede cambiar los criterios de este negocio. Solo el dueño (o Victorix) los cambia.";
  if (code === "P0033") return "La calificación no está encendida para este negocio.";
  if (code === "42704") return "Esa versión ya no está en la lista. Vuelve a entrar para ver las que hay.";
  if (code === "22023" && typeof e?.message === "string" && e.message) return `No se guardó: ${e.message}.`;
  return "No se pudo guardar. Vuelve a intentarlo en un momento; los criterios siguen como estaban.";
}

export function Criterios({ ctx }: { ctx: ContextoPanel }) {
  const { sb, tenantId, rol, usuarioId } = ctx;
  const esDueno = rol === "dueno";
  const puedeEditar = rol === "victorix" || rol === "dueno";

  const [fila, setFila] = useState<FilaCriterios | null>(null);
  const [versiones, setVersiones] = useState<VersionCriterios[]>([]);
  const [original, setOriginal] = useState<Borrador | null>(null);
  const [borrador, setBorrador] = useState<Borrador | null>(null);
  const [nota, setNota] = useState("");
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [refresco, setRefresco] = useState(0);
  // ⭐ T-CAL-11 · null = no se pudo saber (no se afirma nada); false = apagada.
  const [vozActiva, setVozActiva] = useState<boolean | null>(null);
  // ⭐ T-CAL-21 · el gasto del mes: null = cargando; "error" = no se pudo leer.
  const [gasto, setGasto] = useState<VozDelMes | "error" | null>(null);
  // A 375 px el botón de guardar queda ABAJO y el aviso nace ARRIBA: sin esto el dueño
  // guarda y no ve ni el «guardado» ni el porqué de un rechazo.
  const avisos = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (aviso || error) avisos.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [aviso, error]);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const f = await leerCriteriosCalificacion(sb, tenantId);
        const vs = f ? await leerVersionesCriterios(sb, tenantId) : [];
        if (!vivo) return;
        setFila(f);
        setVersiones(vs);
        const b = f ? aBorrador(f.criterios) : null;
        setOriginal(b);
        setBorrador(b);
        setError(null);
      } catch {
        if (vivo) setError("No se pudieron cargar los criterios. Vuelve a entrar en un momento.");
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, [sb, tenantId, refresco]);

  // ⭐ T-CAL-11 · por SEPARADO y con su propio `catch`: que no se pueda leer el
  //   encendido de la voz no le cuesta al dueño la pantalla de criterios.
  useEffect(() => {
    let vivo = true;
    leerVozDelNegocio(sb, tenantId)
      .then((v) => { if (vivo) setVozActiva(v); })
      .catch(() => { if (vivo) setVozActiva(null); });
    return () => { vivo = false; };
  }, [sb, tenantId]);

  // ⭐ T-CAL-21 · I-182 · el gasto del mes, SOLO para el dueño y Victorix (al agente ni se
  //   le pide: la base le diría 42501). Con su propio `catch`: si falla, la pestaña sigue.
  useEffect(() => {
    if (!puedeEditar) return;
    let vivo = true;
    leerVozDelMes(sb, tenantId)
      .then((g) => { if (vivo) setGasto(g); })
      .catch(() => { if (vivo) setGasto("error"); });
    return () => { vivo = false; };
  }, [sb, tenantId, puedeEditar, refresco]);

  const cambios = original && borrador ? cambiosDeCriterios(original, borrador) : null;
  const hayCambios = !!cambios && (cambios.error !== null || Object.keys(cambios.editables).length > 0);

  async function alGuardar() {
    if (!cambios || ocupado || !puedeEditar) return;
    setError(null); setAviso(null);
    if (cambios.error) { setError(cambios.error); return; }
    setOcupado(true);
    try {
      const v = await guardarCriterios(sb, tenantId, cambios.editables, nota.trim() || null);
      setAviso(`Guardada la versión ${v}. El bot usa los nuevos criterios desde el próximo mensaje de cada cliente.`);
      setNota("");
      setRefresco((n) => n + 1);
    } catch (e) {
      setError(explicarFallo(e));
    } finally {
      setOcupado(false);
    }
  }

  async function alVolver(version: number) {
    if (ocupado || !puedeEditar) return;
    setOcupado(true); setError(null); setAviso(null);
    try {
      const v = await volverACriterios(sb, tenantId, version);
      setAviso(`Volviste a los criterios de la versión ${version} (quedó como la versión ${v}). El bot los usa desde el próximo mensaje de cada cliente.`);
      setRefresco((n) => n + 1);
    } catch (e) {
      setError(explicarFallo(e));
    } finally {
      setOcupado(false);
    }
  }

  const cambiarPunto = (llave: string, v: string) => setBorrador((b) => b && ({ ...b, puntos: { ...b.puntos, [llave]: v } }));
  const cambiarSuelto = (llave: Sueltos, v: string) => setBorrador((b) => b && ({ ...b, sueltos: { ...b.sueltos, [llave]: v } }));
  const cambiarMomento = (llave: Momento, m: Partial<BorradorMomento>) =>
    setBorrador((b) => b && ({ ...b, momentos: { ...b.momentos, [llave]: { ...b.momentos[llave], ...m } } }));
  // ⭐ T-CAL-21 · con momentos, la puerta por temperatura ya no manda (worker `puedeHablar`):
  //   el selector se oculta. Se mira el BORRADOR: encender un momento lo oculta al instante.
  const conMomentos = !!borrador && Object.keys(momentosEfectivos(borrador)).length > 0;

  return (
    <div className="h-full flex flex-col min-w-0" data-criterios>
      <header className="shrink-0 px-4 sm:px-6 pt-4 pb-3 border-b border-borde-suave">
        <h1 className="text-[19px] font-semibold tracking-tight">Cómo se califica a un cliente</h1>
        <p className="text-[12.5px] text-tinta-2 mt-0.5">
          Cuántos puntos da cada dato que el cliente cuenta, y desde cuántos puntos es 🔥 o tibio.
        </p>
      </header>

      <div className="flex-1 overflow-y-auto scroll-fino px-4 sm:px-6 py-4">
        <div className="max-w-[680px] mx-auto w-full space-y-3 min-w-0">
          <div ref={avisos} className="space-y-3 scroll-mt-4">
            {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}
            {aviso && <Aviso tono="ok" onCerrar={() => setAviso(null)}>{aviso}</Aviso>}
          </div>

          {cargando && <SkeletonLista filas={4} />}

          {!cargando && !fila && !error && (
            <div data-sin-criterios className="border border-borde rounded-xl bg-superficie p-5 text-center">
              <p className="text-[14px] font-semibold">La calificación no está encendida para este negocio</p>
              <p className="text-[12.5px] text-tinta-2 mt-1.5 leading-snug">
                Mientras no lo esté, el bot no le pone temperatura a nadie. La enciende Victorix.
              </p>
            </div>
          )}

          {!cargando && fila && borrador && (<>
            <Aviso tono="alerta">
              {esDueno
                ? <>Estos criterios los cambias tú. Cada vez que guardas nace una versión NUEVA y
                    puedes volver a cualquiera desde la lista de abajo. El bot los usa desde el
                    próximo mensaje de cada cliente.</>
                : puedeEditar
                  ? <>Cada cambio se guarda como una versión NUEVA y se puede volver a cualquiera.
                      El bot los usa desde el próximo mensaje de cada cliente.</>
                  : <span data-porque-solo-lectura>Esta pantalla es para mirar: los criterios los cambia el
                      dueño del negocio (o Victorix), porque deciden a qué clientes se atiende primero.</span>}
            </Aviso>

            <section data-seccion="puntos" className="border border-borde rounded-xl bg-superficie p-4">
              <h2 className="text-[14px] font-semibold">Qué suma puntos</h2>
              <p className="text-[12.5px] text-tinta-2 mt-0.5 leading-snug">
                Cada dato que el cliente dice le suma estos puntos. Con 0, ese dato no cuenta.
              </p>
              <ul className="mt-2 divide-y divide-borde-suave">
                {CRITERIOS_PUNTOS.map((c) => (
                  <Renglon key={c.llave} dato={c.llave} nombre={c.nombre}
                    detalle={c.llave === "producto"
                      ? `Por ejemplo: ${c.ejemplo}. Siempre se pregunta: sin producto nadie es 🔥`
                      : `Por ejemplo: ${c.ejemplo}`}
                    valor={borrador.puntos[c.llave]} unidad="puntos" min={c.llave === "producto" ? 1 : 0} max={100}
                    editable={puedeEditar} onCambiar={(v) => cambiarPunto(c.llave, v)} />
                ))}
              </ul>
            </section>

            <section data-seccion="umbrales" className="border border-borde rounded-xl bg-superficie p-4">
              <h2 className="text-[14px] font-semibold">Desde cuántos puntos es 🔥 y desde cuántos es tibio</h2>
              <ul className="mt-2 divide-y divide-borde-suave">
                <Renglon dato="caliente" nombre="🔥 desde" detalle="Con estos puntos o más, el cliente es 🔥"
                  valor={borrador.caliente} unidad="puntos" min={1} max={1000}
                  editable={puedeEditar} onCambiar={(v) => setBorrador((b) => b && ({ ...b, caliente: v }))} />
                <Renglon dato="tibio" nombre="Tibio desde" detalle="Con estos puntos o más (y menos que 🔥), es tibio; por debajo, frío"
                  valor={borrador.tibio} unidad="puntos" min={1} max={1000}
                  editable={puedeEditar} onCambiar={(v) => setBorrador((b) => b && ({ ...b, tibio: v }))} />
              </ul>
              <p data-regla-dura className="text-[12.5px] text-tinta-2 mt-2 leading-snug">
                Para ser 🔥 además tiene que haber dicho qué producto quiere y pedido cotización.
              </p>
            </section>

            <section data-seccion="tiempos" className="border border-borde rounded-xl bg-superficie p-4">
              <h2 className="text-[14px] font-semibold">Cuándo se enfría y cuándo se duerme</h2>
              <ul className="mt-2 divide-y divide-borde-suave">
                {SUELTOS.map((s) => (
                  <Renglon key={s.llave} dato={s.llave} nombre={s.nombre}
                    valor={borrador.sueltos[s.llave]} unidad={s.unidad} min={s.min} max={s.max}
                    editable={puedeEditar} onCambiar={(v) => cambiarSuelto(s.llave, v)} />
                ))}
              </ul>
            </section>

            <section data-seccion="voz" className="border border-borde rounded-xl bg-superficie p-4 min-w-0">
              <h2 className="text-[14px] font-semibold">Notas de voz</h2>
              <p className="text-[12.5px] text-tinta-2 mt-0.5 leading-snug">
                En qué momentos de la conversación el bot manda una nota de voz y qué dice en ella.
              </p>
              {vozActiva === false && (
                <p data-voz-apagada className="text-[12.5px] text-tinta-2 mt-2 leading-snug">
                  La voz está apagada para este negocio: Victorix la enciende tras elegir la voz.
                </p>
              )}

              <div className="mt-3 space-y-2.5">
                {MOMENTOS_VOZ.map((m) => (
                  <TarjetaMomento key={m.llave} llave={m.llave} nombre={m.nombre} corto={m.corto}
                    valor={borrador.momentos[m.llave]} editable={puedeEditar}
                    onCambiar={(cambio) => cambiarMomento(m.llave, cambio)} />
                ))}
              </div>

              <div data-voz-ab className="mt-3 border-t border-borde-suave pt-2">
                <Interruptor activo={borrador.vozAb} deshabilitado={!puedeEditar} testId="voz_ab"
                  etiqueta="Comparar audio con texto"
                  detalle="A la mitad de los clientes les llega audio y a la otra mitad solo texto; cada mensaje queda marcado para comparar después qué vende más."
                  onCambiar={(v) => setBorrador((b) => b && ({ ...b, vozAb: v }))} />
              </div>

              <ul className="mt-1 divide-y divide-borde-suave border-t border-borde-suave">
                {!conMomentos && (
                  <SelectorVoz valor={borrador.vozDesde} editable={puedeEditar}
                    onCambiar={(v) => setBorrador((b) => b && ({ ...b, vozDesde: v }))} />
                )}
                <Renglon dato="voz_max_por_dia" nombre="Máximo de audios por cliente al día"
                  detalle="Con 0, el bot no manda audios"
                  valor={borrador.vozMax} unidad="audios" min={0} max={10}
                  editable={puedeEditar} onCambiar={(v) => setBorrador((b) => b && ({ ...b, vozMax: v }))} />
              </ul>

              <div data-voz-fijo className="mt-3 rounded-lg bg-elevado px-3 py-2.5">
                <p className="text-[12.5px] font-medium">Lo que no se puede cambiar</p>
                <ul className="mt-1 list-disc pl-4 space-y-0.5 text-[12px] text-tinta-2 leading-snug">
                  {VOZ_FIJO.map((f) => <li key={f}>{f}</li>)}
                </ul>
              </div>

              {puedeEditar && <GastoDelMes gasto={gasto} />}
            </section>

            {puedeEditar && (
              <div className="flex flex-wrap items-end justify-end gap-3 pt-1">
                <label className="flex-1 min-w-[180px] text-[12.5px] text-tinta-2">
                  Nota (opcional)
                  <input type="text" value={nota} onChange={(e) => setNota(e.target.value)}
                    aria-label="Nota del cambio" placeholder="p. ej. subo la ciudad porque…"
                    className="mt-1 w-full h-9 px-3 rounded-lg bg-elevado text-tinta text-sm border border-borde" />
                </label>
                <Boton tono="marca" onClick={alGuardar} disabled={!hayCambios || ocupado}>
                  Guardar criterios
                </Boton>
              </div>
            )}

            <ListaDeVersiones versiones={versiones} usuarioId={usuarioId}
              puedeVolver={puedeEditar} ocupado={ocupado} onVolver={alVolver} />
          </>)}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Un criterio: su nombre en palabras y su número. Para el agente, la caja deshabilitada. */
function Renglon({ dato, nombre, detalle, valor, unidad, min, max, editable, onCambiar }: {
  dato: string; nombre: string; detalle?: string; valor: string; unidad: string;
  min: number; max?: number; editable: boolean; onCambiar: (v: string) => void;
}) {
  const id = useId();
  return (
    <li data-criterio={dato} className="flex items-center gap-3 py-2 min-w-0">
      <label htmlFor={id} className="min-w-0 flex-1">
        <span className="block text-[13px] font-medium">{nombre}</span>
        {detalle && <span className="block text-[11.5px] text-tinta-2 leading-snug">{detalle}</span>}
      </label>
      <span className="shrink-0 flex items-center gap-1.5">
        <input id={id} type="number" inputMode="numeric" step={1} min={min} max={max}
          value={valor} disabled={!editable} aria-label={nombre}
          onChange={(e) => onCambiar(e.target.value)}
          className="w-[72px] h-9 px-2 rounded-lg bg-elevado text-tinta text-sm text-right border border-borde
                     disabled:opacity-60 disabled:cursor-not-allowed" />
        <span className="w-[58px] text-[11.5px] text-tinta-2">{unidad}</span>
      </span>
    </li>
  );
}

/**
 * ⭐ T-CAL-21 · un momento de la voz: encendido/apagado + qué debe decir el audio (1…300,
 * con contador). El motivo de «no se puede guardar así» va AL LADO de la caja.
 */
function TarjetaMomento({ llave, nombre, corto, valor, editable, onCambiar }: {
  llave: string; nombre: string; corto: string; valor: BorradorMomento; editable: boolean;
  onCambiar: (m: Partial<BorradorMomento>) => void;
}) {
  const id = useId();
  const motivo = motivoDelMomento(valor);
  const largo = [...valor.guion].length; // H-2 r1: igual que char_length de la base (un emoji = 1)
  return (
    <div data-momento={llave} className="border border-borde-suave rounded-lg px-3 pb-3 pt-1 min-w-0">
      <Interruptor activo={valor.activa} deshabilitado={!editable} testId={`momento-${llave}`}
        etiqueta={nombre} detalle={valor.activa ? "Encendido" : "Apagado"}
        onCambiar={(v) => onCambiar({ activa: v })} />
      <label htmlFor={id} className="block text-[12.5px] font-medium mt-1">Qué debe decir el audio</label>
      {/* ⚠️ Alto según el largo (≈38 caracteres por línea a 375 px): con 3 filas fijas, un guion
          de 145 quedaba cortado en el celular y había que hacer scroll DENTRO de la caja. */}
      <textarea id={id} value={valor.guion} disabled={!editable}
        rows={Math.min(9, Math.max(3, Math.ceil(valor.guion.length / 38) + 1))}
        aria-label={`Qué debe decir el audio · ${corto}`} aria-invalid={motivo ? true : undefined}
        onChange={(e) => onCambiar({ guion: e.target.value })}
        placeholder="Por ejemplo: recomienda el producto que pidió, por qué y su garantía."
        className={`mt-1 w-full min-w-0 text-[13px] leading-relaxed bg-elevado border rounded-lg p-2.5
                    scroll-fino resize-y break-words disabled:opacity-60 disabled:cursor-not-allowed
                    ${motivo ? "border-peligro" : "border-borde"}`} />
      <div className="mt-1 flex items-start justify-between gap-3 min-w-0">
        <p data-motivo className="min-w-0 text-[12px] text-peligro leading-snug break-words">{motivo ?? ""}</p>
        <span data-contador className={`shrink-0 text-[11.5px] tabular-nums ${largo > GUION_MAX ? "text-peligro font-semibold" : "text-tinta-2"}`}>
          {largo}/{GUION_MAX}
        </span>
      </div>
    </div>
  );
}

/** ⭐ T-CAL-21 · I-182 · «Este mes»: notas del bot contra el presupuesto, el costo y por momento. */
function GastoDelMes({ gasto }: { gasto: VozDelMes | "error" | null }) {
  if (gasto === null) return null;
  if (gasto === "error") {
    return (
      <div data-voz-del-mes className="mt-3 border-t border-borde-suave pt-2.5">
        <p className="text-[12.5px] font-medium">Este mes</p>
        <p data-gasto-error className="text-[12.5px] text-tinta-2 mt-0.5">No se pudo leer el gasto del mes.</p>
      </div>
    );
  }
  const usd = gasto.costo_mes_usd === null ? null
    : gasto.costo_mes_usd.toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div data-voz-del-mes className="mt-3 border-t border-borde-suave pt-2.5">
      <p className="text-[12.5px] font-medium">Este mes</p>
      <p data-gasto-total className="text-[13px] mt-0.5 break-words">
        {gasto.max_mes === null ? `${gasto.audios_mes} audios del mes` : `${gasto.audios_mes} de ${gasto.max_mes} audios del mes`}
        {usd !== null && ` · $${usd} USD`}
      </p>
      <p data-gasto-momentos className="text-[12px] text-tinta-2 mt-0.5 break-words">
        Recomendación: {gasto.por_momento.recomendacion} · Compra: {gasto.por_momento.compra}
      </p>
    </div>
  );
}

/** ⭐ T-CAL-11 · desde cuándo habla el bot: nunca / tibio / 🔥. Para el agente, deshabilitado. */
function SelectorVoz({ valor, editable, onCambiar }: {
  valor: string; editable: boolean; onCambiar: (v: string) => void;
}) {
  const id = useId();
  return (
    <li data-criterio="voz_desde" className="flex items-center gap-3 py-2 min-w-0">
      <label htmlFor={id} className="min-w-0 flex-1">
        <span className="block text-[13px] font-medium">Desde cuándo habla el bot</span>
        <span className="block text-[11.5px] text-tinta-2 leading-snug">Según la temperatura del cliente</span>
      </label>
      <select id={id} value={valor} disabled={!editable} aria-label="Desde cuándo habla el bot"
        onChange={(e) => onCambiar(e.target.value)}
        className="shrink-0 h-9 px-2 rounded-lg bg-elevado text-tinta text-sm border border-borde
                   disabled:opacity-60 disabled:cursor-not-allowed">
        {VOZ_DESDE.map((v) => <option key={v.valor} value={v.valor}>{v.nombre}</option>)}
      </select>
    </li>
  );
}

/**
 * Las versiones. La más nueva es la vigente: se marca y no ofrece «volver» (no hay a
 * dónde volver desde donde ya se está). Ninguna se borra.
 */
function ListaDeVersiones({ versiones, usuarioId, puedeVolver, ocupado, onVolver }: {
  versiones: VersionCriterios[]; usuarioId: string | null; puedeVolver: boolean; ocupado: boolean;
  onVolver: (version: number) => void;
}) {
  if (versiones.length === 0) return null;
  const vigente = Math.max(...versiones.map((v) => v.version));
  return (
    <section data-versiones className="border border-borde rounded-xl bg-superficie p-4 mt-2">
      <h2 className="text-[14px] font-semibold">Versiones</h2>
      <p className="text-[12.5px] text-tinta-2 mt-0.5 leading-snug">
        Ninguna se borra. Volver a una deja una versión nueva igual a ella.
      </p>
      <ul className="mt-2.5 divide-y divide-borde-suave">
        {versiones.map((v) => (
          <li key={v.version} data-version={v.version} className="flex items-center gap-3 py-2 min-w-0">
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-medium">
                Versión {v.version}
                {v.version === vigente && (
                  <span data-vigente className="ml-2 text-[11px] font-semibold text-marca align-middle">Vigente</span>
                )}
              </p>
              <p className="text-[12px] text-tinta-2 break-words">
                {fechaYHora(v.creado_en)} · la dejó {quienLaDejo(v, usuarioId)}
                {v.nota ? <> · «{v.nota}»</> : null}
              </p>
            </div>
            {puedeVolver && v.version !== vigente && (
              <Boton tam="sm" tono="neutro" disabled={ocupado} onClick={() => onVolver(v.version)}>
                <RotateCcw size={14} className="mr-1.5" />
                Volver a esta
              </Boton>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
